/**
 * Queen B Academy - Freight Broker 101 Enhanced Content
 * Extended lessons with real-world examples, exercises, pro tips, and actionable content
 */

export const FREIGHT_BROKER_ENHANCED = {
  courseId: "freight-broker-101",
  courseTitle: "Freight Broker 101",
  enhancedModules: {
    // ============================================
    // MODULE 1: BROKER ROLE & RESPONSIBILITIES
    // ============================================
    "broker-m1": {
      moduleTitle: "Broker Role & Responsibilities",
      lessons: {
        "broker-m1-l1": {
          title: "The Freight Brokerage Opportunity - Complete Business Guide",
          duration: "55 min",
          content: `# Becoming a Freight Broker: Your Guide to a Six-Figure Business

## What Is a Freight Broker?

A freight broker is the **middleman** connecting shippers who have freight with carriers who can move it. You don't own trucks. You don't touch freight. You make money by matching supply with demand efficiently.

Think of yourself as a real estate agent for freight - you bring together parties who need each other and facilitate the transaction.

### The Value You Provide

**To Shippers:**
- Find reliable carriers quickly
- Vet carriers for insurance and safety
- Handle documentation and tracking
- Solve problems when they arise
- Single point of contact for freight needs

**To Carriers:**
- Find available loads matching their equipment
- Reduce deadhead and downtime
- Handle shipper relationships and paperwork
- Quick payment options
- Consistent freight opportunities

---

## The Money: How Freight Brokerage Works

### The Simple Math

| Role | Amount |
|------|--------|
| Shipper pays you | $4,000 |
| You pay carrier | $3,200 |
| **Your gross margin** | **$800 (20%)** |

Do that 10 times a week and you're looking at $8,000/week in gross margin.
That's **$400,000+ gross revenue annually** from a laptop and phone.

### Revenue Models

**Spot Market (Per-Load)**
- Each load is individually negotiated
- Higher margins but more work
- Typical margin: 12-25%
- Great for new brokers building business

**Contract Freight (Committed)**
- Ongoing agreements with shippers
- Lower margins but predictable
- Typical margin: 8-15%
- Builds stable revenue base

**Hybrid Approach (Most Common)**
- Mix of spot and contract
- Provides stability + upside
- Typical overall margin: 12-18%

### Realistic Income Progression

| Stage | Monthly Revenue | Monthly Expenses | Net Income |
|-------|----------------|------------------|------------|
| Year 1 (building) | $15,000-$40,000 | $3,000-$6,000 | $10,000-$30,000 |
| Year 2 (established) | $50,000-$100,000 | $8,000-$15,000 | $35,000-$80,000 |
| Year 3+ (scaled) | $100,000-$300,000 | $15,000-$50,000 | $75,000-$200,000+ |

**Real-World Success Story:**

*Meet David, freight broker since 2021:*

"I left my logistics coordinator job making $48,000 a year to start my brokerage. First three months were rough - I made maybe $8,000 total. But I kept calling, kept building relationships.

By month six, I was making more than my old salary. By year two, I cleared $145,000. Now in year three, I have two employees and we're on track for $3.2 million in revenue with about $480,000 in gross margins.

The best part? My initial investment was $4,500 total. The ROI is unreal if you're willing to put in the work."

---

## Why Freight Brokerage?

### Low Startup Costs

Unlike trucking or warehousing:
- No trucks to buy or maintain
- No drivers to hire or manage
- No fuel costs
- No warehouse or dock needed
- Home office is perfectly viable

**Total startup costs: $3,000-$10,000**

### High Income Ceiling

- No cap on how much freight you move
- Scales with your effort and team
- Six figures very achievable
- Million-dollar brokerages exist everywhere

### Lifestyle Flexibility

- Work from anywhere with phone and laptop
- Set your own hours (within reason)
- No physical labor
- Build equity in relationships and systems

### Recession Resistance

- Freight moves in all economic conditions
- Spot market often improves in downturns (carrier capacity)
- Essential industry - always in demand

---

## The Business Model Explained

### Your Position in the Supply Chain

\`\`\`
MANUFACTURER → SHIPPER → BROKER (You) → CARRIER → RECEIVER
                              ↓
                    Value Add:
                    - Find capacity
                    - Vet carriers
                    - Handle docs
                    - Track shipments
                    - Solve problems
\`\`\`

### The Transaction Flow

**Step 1: Shipper has freight**
- Company needs to move product
- Internal logistics or reaches out to you

**Step 2: You quote the shipper**
- Research carrier costs for the lane
- Add your margin
- Present all-in rate to shipper

**Step 3: Shipper accepts**
- You confirm the booking
- Shipment details locked in
- Now you need a carrier

**Step 4: You find a carrier**
- Search your network
- Post on load boards if needed
- Negotiate carrier rate
- Book and confirm

**Step 5: Execution**
- Monitor pickup and delivery
- Handle any issues that arise
- Collect POD (proof of delivery)

**Step 6: Settlement**
- Invoice shipper
- Pay carrier (when you receive payment)
- Your margin is the profit

---

## Legal Requirements

### Freight Broker Authority

**What It Is:**
- Federal license to arrange transportation
- NOT the same as motor carrier authority
- Issued by FMCSA
- Required before you can legally broker freight

**How to Get It:**
1. File OP-1 application at FMCSA portal
2. Pay $300 filing fee
3. Wait 4-6 weeks for processing
4. Complete additional requirements

### Surety Bond (BMC-84)

**What It Is:**
- $75,000 financial guarantee
- Protects shippers and carriers if you don't pay
- Filed with FMCSA before authority activates

**What It Costs:**
| Credit Score | Annual Premium |
|--------------|----------------|
| 700+ (excellent) | $750-$1,500 |
| 650-699 (good) | $1,500-$2,500 |
| 600-649 (fair) | $2,500-$4,500 |
| Below 600 | May require trust fund |

**How It Works:**
- You pay annual premium to surety company
- Surety provides $75,000 bond to FMCSA
- If you don't pay carriers/shippers, they file claim
- Surety pays them, then comes after you

### Process Agent (BOC-3)

**What It Is:**
- Agent designated to receive legal papers in each state
- Required for all authorities

**What It Costs:**
- $30-$100 for blanket coverage (all states)
- One-time filing (unless agent changes)

### UCR Registration

**What It Is:**
- Unified Carrier Registration
- Annual registration required

**What It Costs:**
- $69+ depending on classification
- Due annually by October 1

---

## Total Legal Startup Costs

| Item | One-Time | Annual |
|------|----------|--------|
| Authority filing | $300 | - |
| BOC-3 | $50 | - |
| Surety bond | - | $900-$3,500 |
| UCR | - | $69 |
| **TOTAL** | **$350** | **$970-$3,570** |

First year total: **$1,320-$3,920**

This is one of the lowest barriers to entry for any business with six-figure potential.

---

## Skills You Need to Develop

### Sales Skills (Most Critical)

You are in sales. Period. The faster you accept this, the faster you'll succeed.

**Cold Calling:**
- Initial shipper outreach
- 80%+ rejection rate is normal
- Skills improve dramatically with practice
- Most new brokers fail here - don't give up

**Relationship Building:**
- Convert first shipments into repeat customers
- Build trust over multiple transactions
- Become a resource, not just a vendor

**Negotiation:**
- Get best rates from shippers
- Get best rates from carriers
- Protect your margins while being fair

### Logistics Knowledge

**Lane Expertise:**
- Know typical rates in your focus areas
- Understand seasonal patterns
- Regional specialization helps

**Equipment Understanding:**
- Different trailer types and uses
- Weight and dimension limitations
- Special requirements (reefer temps, tarps, etc.)

**Compliance Basics:**
- Hours of service awareness
- Weight regulations
- Hazmat basics

### Technology Proficiency

**Essential Tools:**
- Load boards (DAT, Truckstop)
- TMS software (or spreadsheets to start)
- Communication tools
- Accounting software

---

## Exercise: Brokerage Business Plan

### Part 1: Your Niche

**What equipment type will you focus on?**
- [ ] Dry van (highest volume, most competition)
- [ ] Reefer (temperature-controlled, higher margins)
- [ ] Flatbed (specialized, fewer brokers)
- [ ] Other: _________________

**What region/lanes will you target?**
- Geographic focus: _________________
- Specific lane(s): _________________

**What industry will you specialize in?**
- [ ] General freight
- [ ] Food/beverage
- [ ] Retail/consumer goods
- [ ] Construction materials
- [ ] Manufacturing
- [ ] Other: _________________

### Part 2: Startup Budget

| Item | Your Budget |
|------|-------------|
| Authority & legal | $ |
| Bond (first year) | $ |
| Load board subscription | $ |
| TMS/software | $ |
| Phone/communication | $ |
| Marketing | $ |
| Operating capital | $ |
| **TOTAL STARTUP** | **$** |

### Part 3: Income Goals

| Milestone | Target | Timeline |
|-----------|--------|----------|
| First load | | Week ___ |
| $5,000 monthly margin | | Month ___ |
| $10,000 monthly margin | | Month ___ |
| Replace current income | | Month ___ |
| $100K annual | | Year ___ |

### Part 4: Competitive Advantage

**What will make shippers choose YOU over other brokers?**

1. _______________________________
2. _______________________________
3. _______________________________

---

## Key Takeaways

- Freight brokerage has low barriers to entry ($3,000-$10,000 startup)
- Legal requirements total under $4,000 first year
- Sales ability is the #1 skill required for success
- Target margins range from 12-25% depending on freight type
- Income potential is unlimited - scales with effort and team
- Reputation and relationships drive long-term profitability
- Most new brokers fail because they give up on sales too early

---

## What's Next?

In the next lesson, we'll dive deep into getting your broker authority - the exact steps, timeline, and how to get operational as quickly as possible.`,
          exercises: [
            {
              type: "business_plan",
              title: "Complete Brokerage Business Plan",
              prompt: "Complete all four parts of the business plan exercise with specific, researched details for your target market.",
              timeEstimate: "1 hour",
              deliverable: "Written business plan"
            },
            {
              type: "research",
              title: "Competitor Analysis",
              prompt: "Identify 3 freight brokers in your target niche. Research their services, specializations, and what you could do differently or better.",
              timeEstimate: "30 minutes",
              deliverable: "Competitor comparison"
            }
          ],
          keyTakeaways: [
            "Freight brokerage requires only $3,000-$10,000 to start legally",
            "Target gross margins range from 12-25% per load",
            "Sales ability is the #1 predictor of success",
            "Legal requirements are straightforward and affordable",
            "Most failures come from giving up on sales too early"
          ],
          proTips: [
            "Start with a niche you have some knowledge of - previous industry experience is gold",
            "Don't quit your day job until you have 3+ months of consistent margin",
            "Build carrier relationships before you desperately need trucks",
            "Your first 100 cold calls will be terrible - call 101"
          ]
        }
      }
    },

    // ============================================
    // MODULE 3: FINDING SHIPPERS
    // ============================================
    "broker-m3": {
      moduleTitle: "Finding Shippers",
      lessons: {
        "broker-m3-l1": {
          title: "Shipper Outreach Strategies That Work",
          duration: "55 min",
          content: `# Finding Shippers: Your Path to Revenue

## The Fundamental Truth

No shippers = no revenue. Everything else in brokerage is secondary to your ability to find and win shipping customers. This is a sales job first, logistics second.

---

## Where to Find Shipper Prospects

### Direct Research Methods

**Company Websites**
- Look for "shipping," "logistics," or "contact" pages
- Check if they have dedicated logistics/transportation roles
- Note their product types and likely shipping needs

**LinkedIn**
- Search for: Logistics Manager, Transportation Coordinator, Supply Chain Manager
- Filter by industry and location
- Join industry groups and engage

**Industry Directories**
- Thomas Register (manufacturing)
- Grocery/food industry directories
- Construction supplier databases
- Import/export databases

**Business Registration Records**
- State business filings
- SBA databases
- Industry association member lists

### Indirect Methods

**Networking**
- Industry associations and trade shows
- Chamber of Commerce events
- Professional networking groups
- Referrals from satisfied customers

**Observation**
- Industrial areas and manufacturing zones
- Distribution centers and warehouses
- Trucks loading/unloading at facilities
- "Now Hiring" signs at logistics companies

**Carrier Referrals**
- Carriers sometimes refer shippers to good brokers
- Build relationships with carriers - they see opportunities
- Offer referral incentives

---

## Cold Calling: The Essential Skill

### Why Cold Calling Works

- Direct, immediate contact with decision-makers
- Higher response rate than email
- Builds phone confidence
- You can handle objections in real-time
- Top brokers make 50-100 calls daily

### The Call Framework

**Opening (10 seconds):**
"Hi, this is [Name] with [Company]. I'm reaching out to shipping managers who handle [freight type] logistics. Is that something you're involved with?"

**If Yes (Qualification):**
"Great. I'm not calling to take over your business - I'm calling to see if there's ever a time when your regular carriers can't cover a load, or you need backup capacity. Would you be open to being on my short list for those situations?"

**If Interest (Information Gathering):**
"Perfect. Just so I can be prepared - what do you typically ship, and where do most loads go?"

**Close:**
"I'd love to send you our carrier packet and be ready when you need us. What's the best email? And who should I follow up with if freight opportunities come up?"

### Handling Common Objections

**"We're happy with our current providers."**
"I totally understand - that's great that you have reliable partners. I'm not looking to replace them. But when capacity gets tight, or your regulars are committed elsewhere, I'd love to be the call you make. Can I send you my info as a backup option?"

**"Send me an email."**
"Absolutely. What's the best email? And quick question before I do - what type of freight do you move most, so I can make sure I'm sending relevant information?"

**"We don't use brokers."**
"Fair enough. A lot of shippers feel that way until capacity gets tight. I'll note that, but would you be open to a quick conversation if that ever changes? What's the best way to follow up in a few months?"

**"Not interested."**
"I appreciate your time. If things ever change, I'd welcome the opportunity to help. Have a great day."

### Call Numbers Reality

Expect these ratios as a new broker:
- 100 calls → 20 conversations → 5 interested → 1-2 customers
- That's 1-2% conversion on calls
- As you improve: 3-5% conversion is achievable
- Top performers: 8-10% conversion

---

## Email Outreach

### When to Use Email

- Follow-up after cold calls
- Introduction before events
- Nurturing leads over time
- Providing requested information

### Email Templates

**Initial Introduction:**

Subject: Backup Capacity for [Company Name] Freight

Hi [Name],

I'm reaching out because I work with several [industry] companies in [region] and wanted to introduce [Your Company] as a potential logistics partner.

We specialize in [your specialty] and maintain a vetted network of carriers for [equipment type].

I'm not looking to replace your current providers - just to be a resource when capacity is tight or you need reliable backup.

Would you be open to a brief call to learn more about your shipping needs?

Best,
[Your Name]
[Phone]

**Follow-Up After Call:**

Subject: Following Up - [Your Company] Introduction

Hi [Name],

Thanks for taking a few minutes with me earlier. As I mentioned, we specialize in [specialty] and would love to be your backup option when capacity gets tight.

I've attached our carrier packet and company overview. When you have a load that needs coverage, give me a call at [phone] - I'll get you quoted quickly.

Looking forward to working together.

Best,
[Your Name]

---

## Building a Client Base

### The Sales Funnel

\`\`\`
PROSPECT (cold contact)
    ↓
QUALIFY (do they ship? decision maker?)
    ↓
CONNECT (conversation, build rapport)
    ↓
DISCOVER (learn their needs, pain points)
    ↓
PRESENT (show how you solve their problems)
    ↓
HANDLE OBJECTIONS (address concerns)
    ↓
CLOSE (get first shipment)
    ↓
DELIVER (execute flawlessly)
    ↓
GROW (expand the relationship)
\`\`\`

### Converting First Shipment to Repeat Customer

**Before the Load:**
- Confirm all details twice
- Over-communicate on carrier selection
- Set clear expectations

**During the Load:**
- Proactive updates (don't wait for them to ask)
- Handle any issues immediately
- Keep them informed of progress

**After Delivery:**
- Confirm successful delivery
- Thank them for the opportunity
- Ask for feedback
- Ask about next shipping needs

**Follow-Up:**
- Check in weekly if they ship regularly
- Monthly touch for irregular shippers
- Quarterly at minimum to stay top of mind

---

## Exercise: Shipper Prospecting Plan

### Part 1: Target List

Identify 20 potential shippers to contact this week:

| # | Company Name | Contact Name | Phone | Email | Industry |
|---|--------------|--------------|-------|-------|----------|
| 1 | | | | | |
| 2 | | | | | |
| ... | | | | | |
| 20 | | | | | |

### Part 2: Call Script Preparation

Write your personal opening script:

"Hi, this is _______ with _______. I'm reaching out to _______ . Is that something you're involved with?"

### Part 3: Tracking System

How will you track your outreach?
- [ ] CRM software (which one: ________)
- [ ] Spreadsheet
- [ ] Other: ________

---

## Key Takeaways

- Cold calling is the fastest path to new customers - embrace it
- Expect 1-2% conversion initially - volume matters
- Position yourself as backup capacity, not a replacement
- Email works best for follow-up, not first contact
- First shipment must be flawless - sets the relationship tone
- Follow up consistently - most business is won on 5th+ contact`,
          exercises: [
            {
              type: "practical",
              title: "Build Your Prospect List",
              prompt: "Complete the 20-shipper prospect list from this lesson using real companies in your target market. Research each contact name and phone number.",
              timeEstimate: "2 hours",
              deliverable: "20-company prospect list with contacts"
            },
            {
              type: "practice",
              title: "Cold Call Practice",
              prompt: "Practice your cold call script out loud 10 times. Then make 5 real practice calls (to anyone - friends, family) to get comfortable with the flow.",
              timeEstimate: "30 minutes",
              deliverable: "Self-assessment of call confidence"
            }
          ],
          keyTakeaways: [
            "Cold calling is essential - there's no shortcut",
            "Expect 1-2% conversion on calls initially - volume is key",
            "Position as backup capacity to reduce objections",
            "First shipment sets relationship tone - execute flawlessly",
            "Most business is won after 5+ contacts - follow up consistently"
          ],
          proTips: [
            "Make calls standing up - it changes your energy",
            "Smile while talking - it comes through in your voice",
            "Time calls for 9-11 AM or 2-4 PM - best reach rates",
            "Track every call - patterns emerge that help you improve"
          ]
        }
      }
    },

    // ============================================
    // MODULE 5: RATE NEGOTIATION
    // ============================================
    "broker-m5": {
      moduleTitle: "Rate Negotiation",
      lessons: {
        "broker-m5-l1": {
          title: "Pricing Strategy and Margin Management",
          duration: "50 min",
          content: `# Pricing Loads: The Art and Science of Margin Management

## The Fundamental Challenge

As a broker, you're negotiating TWO rates on every load:
1. **Shipper rate** - What you charge for the service
2. **Carrier rate** - What you pay to move the freight

Your margin is the difference. Too aggressive on either side and you lose the deal.

---

## Understanding Carrier Costs

Before you can price a load, you need to know what carriers need.

### Carrier Cost Structure

| Cost Category | Typical Per-Mile |
|---------------|------------------|
| Fuel | $0.50-$0.80 |
| Driver pay | $0.40-$0.60 |
| Insurance | $0.15-$0.25 |
| Equipment/depreciation | $0.15-$0.25 |
| Maintenance | $0.10-$0.20 |
| Overhead | $0.10-$0.15 |
| **Total carrier cost** | **$1.40-$2.25** |

**Translation:** Most carriers need $1.75-$2.25/mile minimum to be profitable.

### Market Factors

| Factor | Effect on Carrier Rates |
|--------|------------------------|
| More loads than trucks | Rates go UP |
| More trucks than loads | Rates go DOWN |
| Fuel price spike | Rates go UP |
| End of month | Rates often UP |
| Holiday weeks | Rates often UP |
| January-February | Typically lower |

---

## Rate Research Tools

### DAT RateView

- Historical rate data by lane
- 15-day, 30-day, 90-day averages
- Market trends and direction
- Essential for competitive pricing

### Truckstop Rate Analysis

- Similar to DAT
- Different data sources
- Good for comparison

### Your Own History

- Track every load you move
- Build lane-specific knowledge
- Know what you've paid before

---

## Pricing Strategy: Shipper Side

### The Quoting Process

**Step 1: Gather Load Details**
- Origin and destination
- Pickup and delivery dates
- Weight and commodity
- Special requirements

**Step 2: Research Carrier Costs**
- Check DAT/Truckstop for lane rates
- Consider current market conditions
- Factor any special requirements

**Step 3: Add Your Margin**
- New broker: 12-15% margin
- Established: 15-20% margin
- Premium service: 20-25%+ margin

**Step 4: Quote Shipper**
- All-in rate or base + FSC
- Clearly state any exclusions
- Provide timeline

### Pricing Example

| Factor | Amount |
|--------|--------|
| Expected carrier cost | $2,800 |
| Your target margin (18%) | $504 |
| **Quote to shipper** | **$3,304** |

Or round to $3,300 for cleaner number.

### When to Quote Higher

- Time-sensitive freight
- Limited capacity in market
- Special equipment needs
- High-service requirements
- Problem shipper premium

### When to Quote Competitive

- Building new relationship
- High-volume potential
- Easy freight (no special requirements)
- Repeat customer loyalty

---

## Pricing Strategy: Carrier Side

### Finding the Right Rate

**Too High:**
- You overpay, margin shrinks
- Can't compete on shipper side

**Too Low:**
- Can't find capacity
- Poor service from unhappy carriers

**Just Right:**
- Market rate + relationship value
- Fair to carrier, profitable for you

### Negotiation Approach with Carriers

**Initial Offer:**
- Slightly below your target
- Leave room for negotiation
- Never insult with lowball

**Sample Dialogue:**
"I've got a load from Dallas to Chicago, picking up tomorrow. It's paying $2.60/mile. Are you interested?"

If pushback: "What would make this work for you?"

**Closing:**
- Confirm all details
- Get driver contact
- Send rate confirmation immediately

---

## Margin Analysis

### Target Margins by Scenario

| Scenario | Target Margin |
|----------|---------------|
| Spot market, new shipper | 15-20% |
| Spot market, regular shipper | 12-18% |
| Contract freight | 8-12% |
| Premium/expedited | 20-30% |
| Distressed freight | 25-40% |

### Margin Calculation

**Gross Margin = (Shipper Rate - Carrier Rate) / Shipper Rate**

Example:
- Shipper pays: $3,500
- Carrier receives: $2,900
- Gross margin: ($3,500 - $2,900) / $3,500 = **17.1%**

### When to Walk Away

- Margin below 8% (rarely worth the risk)
- Shipper won't budge and carrier costs are fixed
- Risk/reward doesn't justify the load
- Bad feeling about the shipment

---

## Exercise: Pricing Practice

### Scenario 1
Load: Chicago to Dallas, dry van, 1,050 miles
DAT shows carrier rate average: $2.35/mile
Your target margin: 16%

Calculate:
- Expected carrier cost: $__________
- Margin addition: $__________
- Shipper quote: $__________

### Scenario 2
Shipper wants rate: $4,200
Best carrier rate you can find: $3,700
Calculate:
- Dollar margin: $__________
- Percentage margin: __________%
- Is this acceptable? Why/why not?

### Scenario 3
Carrier needs $2,900 minimum
Your shipper relationship would suffer above $3,300
Calculate:
- Available margin: $__________
- Percentage: __________%
- Your decision: ____________________

---

## Key Takeaways

- Know carrier costs before pricing to shippers
- Use rate research tools - never guess
- Target 12-20% margin depending on situation
- Build in room for negotiation on both sides
- Walk away from loads that don't make margin sense
- Track every load to build lane expertise`,
          exercises: [
            {
              type: "calculation",
              title: "Pricing Scenarios",
              prompt: "Complete all three pricing scenarios in this lesson, showing your work and reasoning.",
              timeEstimate: "20 minutes",
              deliverable: "Completed calculations with decisions"
            }
          ],
          keyTakeaways: [
            "Research carrier rates BEFORE quoting shippers",
            "Target 12-20% margin depending on freight type",
            "Leave negotiation room on both shipper and carrier rates",
            "Track every load to build proprietary lane data",
            "Walk away from loads below 8% margin - not worth the risk"
          ],
          proTips: [
            "Check rates at multiple times of day - prices move",
            "Thursday/Friday loads for Monday delivery often pay premium",
            "Build relationships with carriers - they'll work with you on rate",
            "Know your floor margin and stick to it - don't chase bad loads"
          ]
        }
      }
    }
  }
};

export default FREIGHT_BROKER_ENHANCED;
